import { useEffect, useRef, useState } from "react";
import Resizer from "react-image-file-resizer";
import moment from "moment";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { generate } from "@ant-design/colors";
import { Tag, message } from "antd";
import liff from "@line/liff";
import * as API from "common/api";
import { SEARCH_DATE_TIME_MAX, SEARCH_DATE_TIME_MIN } from "./constants";
import { omitBy, isNil, isEmpty, differenceWith } from "lodash";

dayjs.extend(customParseFormat);

export const IS_CATEGORY_VERSION =
	import.meta.env.VITE_APP_CATEGORY_VERSION === "true";

/* ROUTES */
export const ADMIN_LOGIN_ROUTE = "/login";
export const ADMIN_HOME_ROUTE = "/home";
export const ADMIN_CATEGORIES_ROUTE = "/categories";
export const ADMIN_OCCASIONS_ROUTE = "/occasions";
export const ADMIN_MEMBERS_ROUTE = "/members";
export const ADMIN_MAINTENANCE_ROUTE = "/maintenance";
export const ADMIN_CAMPAIGN_MEMBERS_ROUTE = "/campaign-members";
export const ADMIN_CAMPAIGN_ROUTE = "/campaign";
export const ADMIN_AUDIENCES_ROUTE = "/audiences";
export const ADMIN_RICHMENU_SETTINGS_ROUTE = "/setting-richmenu";
export const ADMIN_INFLOW_SOURCE_ANALYSIS_ROUTE = "/inflow-source-analysis";

export const ADMIN_SYSTEM_SETTINGS_ROUTE = "/settings/system";
export const ADMIN_EVENT_SETTINGS_ROUTE = "/settings/event";
export const ADMIN_NOTIFICATION_SETTINGS_ROUTE = "/settings/notification";

export const ADMIN_CARD_SETTINGS_ROUTE = "/settings/card-membership";
export const ADMIN_SURVEY_ROUTE = "/surveys";
export const ADMIN_INQUIRIES_ROUTE = "/inquiries";
export const ADMIN_CUSTOMERS_ROUTE = "/customers";
export const ADMIN_LOTTERY_ROUTE = "/lottery";
export const ADMIN_COUPONS_ROUTE = "/coupons";
export const ADMIN_STAMPS_ROUTE = "/stamps";

export const CLIENT_ROUTE = "/liff";
export const CLIENT_LOTTERY_ROUTE = "/liff/lottery";
export const CLIENT_LOGIN_ROUTE = "/liff/login";
export const CLIENT_REGISTER_ROUTE = "/liff/register";
export const CLIENT_CAMPAIGN_ROUTE = "/liff/campaign";
export const CLIENT_CAMPAIGNS_ROUTE = "/liff/campaigns";
export const CLIENT_MEMBERSHIP_ROUTE = "/liff/membership";
export const CLIENT_CATEGORIES_ROUTE = "/liff/categories";
export const CLIENT_OCCASIONS_ROUTE = "/liff/occasions";
export const CLIENT_OCCURRENCES_ROUTE = "/liff/occurrences";
export const CLIENT_RESERVATION_ROUTE = "/liff/reservation";
export const CLIENT_REGISTRATIONS_ROUTE = "/liff/registrations";
export const CLIENT_SURVEY_ROUTE = "/app/surveys";
export const CLIENT_SURVEY_RECORD_ROUTE = "/liff/surveys/record";
export const CLIENT_CAMPAIGN_REGISTRATIONS_ROUTE =
	"/liff/campaign-registrations";
export const CLIENT_REGISTRATION_DETAIL_ROUTE = "/liff/registration/detail";
export const CLIENT_CAMPAIGN_REGISTRATION_DETAIL_ROUTE =
	"/liff/campaign-registration/detail";
export const CLIENT_PROFILE_UPDATE_ROUTE = "/liff/update";
export const CLIENT_TERMS_ROUTE = "/liff/terms";
export const CLIENT_SUBSCRIBE_ROUTE = "/liff/subscribe";
export const CLIENT_SUBSCRIBE_SUCCESS_ROUTE = "/liff/subscribe-success";

export const LINE_ACCESS_ROUTE = "/line-access";
export const LINE_FRIEND_ROUTE = "/line-friend";
export const NOT_FOUND_ROUTE = "/404";
export const PERMISSION_ERROR_ROUTE = "/401";

/* ALERT MESSAGES */
export const SUCCESS_SAVE_MSG = "保存しました。";
export const ERROR_SYSTEM_MSG = "システムエラー";
export const ERROR_UNIQUE_BOTH_MSG =
	"電話番号または顧客コードはすでに登録されています";
export const ERROR_SYSTEM_IM_MSG = "ファイルサイズ：5MB以下";
export const ERROR_SYSTEM_EXIST_MSG = "このアンケートは既に回答されています。";
export const ERROR_SESSION_MSG = "もう一度ログインしてください";
export const ERROR_400_MSG = "無効なリクエストです。";
export const ERROR_401_MSG = "許可が足りないです。";
export const ERROR_404_MSG = "アクセスしたページは存在しません";
export const ERROR_LINE_403_MSG = "LINEアプリからアクセスしてください";
export const ERROR_LINE_FRIEND_MSG = "LINEで友だち追加してアクセスしてください";
export const ERROR_ADMIN_LOGIN_MISMATCH_MSG =
	"ユーザー名またはパスワードが間違っています";
export const ERROR_TELEPHONE_UNIQUE_MSG =
	"この電話番号はすでに登録されています";
export const ERROR_EMAIL_UNIQUE_MSG =
	"このメールアドレスはすでに登録されています";
export const ERROR_TELEPHONE_EMAIL_UNIQUE_MSG =
	"電話番号またはメールアドレスがすでに登録されています";
export const WARN_POSTAL_CODE_WRONG_MSG = "郵便番号を確認してください";
export const WARN_AUDIENCE_NAME_EXIST_MSG =
	"このオーディエンス名はすでに存在しています";
export const WARN_AUDIENCE_COUNT_ZERO_MSG =
	"応募者0人でオーディエンスを作成することはできません";
export const WARN_AUDIENCE_NOT_MATCH_MSG =
	"検索条件が変更されています。一度検索ボタンクリックしてその後作成してください。";
export const WARN_IMAGE_TOO_BIG = "画像サイズが大きすぎます";
export const WARN_IMAGE_SIZE_DIFFERENT = "画像サイズが違います";
export const WARN_CUSTOMER_CODE_NOT_EXIST_MSG =
	"このコードを持つユーザーはありません";
export const WARN_RESERVATION_FULL_MSG =
	"選択した時間帯が満員になったので別の時間帯を選択してください";
export const WARN_RESERVATION_PAST_MSG = "過去に予約を登録することはできません";
export const WARN_CANDIDATES_COUNT_ZERO_MSG =
	"0人から当選者を抽出ことはできません";
export const WARN_PASSWORD_NOT_MATCH_MSG = "パスワードが間違っています";
export const INFO_CAMERA_PERMISSION_MSG = "カメラの許可をお願いします";
export const SUCCESS_LOGIN_MSG = "ログインしました。";
export const SUCCESS_LOGOUT_MSG = "ログアウトしました。";
export const SUCCESS_CREATE_MSG = "作成しました。";
export const SUCCESS_REGISTER_MSG = "登録しました。";
export const SUCCESS_ADD_MSG = "追加しました。";
export const SUCCESS_UPDATE_MSG = "保存しました。";
export const SUCCESS_DELETE_MSG = "削除しました。";
export const SUCCESS_CANCEL_MSG = "キャンセルしました。";
export const SUCCESS_UPLOAD_MSG = "アプロードしました。";
export const SUCCESS_AUDIENCE_SYNC_MSG = "オーディエンス同期しました。";
export const SUCCESS_APPROVED_MSG = "リクエストを受け取りました。";
export const SUCCESS_REJECTED_MSG = "リクエストを断りました。";
export const SUCCESS_MESSAGE_SENT_MSG = "メッセージを送信しました。";
export const SUCCESS_CANDIDATES_SELECT_MSG = "当選者を抽出しました。";
export const SUCCESS_CANDIDATES_RESET_MSG = "当選リストをリセットしました。";
export const SUCCESS_SEARCH_MSG = "検索完了しました。";
export const ERROR_QR_WRONG_MSG = "QRコードが違います、再度ご確認ください";
export const WARN_MEMBER_CODE_NOT_EXIST_MSG =
	"この会員コードを持つユーザーはありません";
export const ERROR_WRONG_SETTING_DISPLAY_CUSTOM_REGISTRATION_MSG =
	"会員証登録フォーム項目表示が「表示」の場合、管理画面項目表示も「表示」に設定してください。";
export const ERROR_TIMEOUT_MSG = `タイムアウトエラー もう一度お試しください`;
export const ERROR_IMAGE_NOT_VALID =
	"アップロードされたファイルは画像形式ではありません。";
export const ERROR_QR_NOT_EXISTS =
	"イベントがキャンセルになったため、このQRコードは無効となります";

/* RESPONSE TYPE */
export const RESPONSE_BAD_REQUEST_ERROR = 400;
export const RESPONSE_PERMISSION_ERROR = 401;
export const RESPONSE_SESSION_ERROR = 403;
export const RESPONSE_NOT_ACCEPTABLE_ERROR = 406;
export const RESPONSE_CONFLICT_ERROR = 409;
export const RESPONSE_SYSTEM_ERROR = 500;
export const RESPONSE_SERVICE_UNAVAILABLE = 503;

/* MESSAGE KEY */
export const MESSAGE_BAD_REQUEST_KEY = "BAD_REQUEST";
export const MESSAGE_SESSION_ERROR_KEY = "SESSION_ERROR";
export const MESSAGE_SYSTEM_ERROR_KEY = "SYSTEM_ERROR";
export const MESSAGE_SERVICE_UNAVAILABLE_KEY = "SERVICE_UNAVAILABLE";

export const ERROR_503_MSG = (retryAfter) => {
	// let seconds;
	// if (retryAfter) {
	// 	if (/^\d+$/.test(retryAfter)) {
	// 		seconds = retryAfter;
	// 	} else {
	// 		const retryDate = moment(retryAfter);
	// 		if (retryDate.isValid()) {
	// 			const diff = Math.ceil(retryDate.diff(moment()) / 1000);
	// 			if (diff > 0) {
	// 				seconds = diff;
	// 			}
	// 		}
	// 	}
	// }
	// if (seconds) {
	// 	return `サーバーが混雑しています。約${seconds}秒後にもう一度お試しください。`;
	// }
	return (
		<span
			style={{
				whiteSpace: "pre-wrap",
				textAlign: "center",
				display: "inline-block",
			}}
		>
			{
				"ただいまアクセスが集中しております。\nしばらく時間を置いてから再度操作していただけますと幸いです。\nご不便をおかけしますが、ご理解とご協力をお願いいたします。"
			}
		</span>
	);
};
export const MESSAGE_CAMERA_PERMISSION_KEY = "CAMERA_PERMISSION";
export const MESSAGE_MAX_FILE_IMAGE_KEY = "File size exceeds maximum";
export const MESSAGE_MAX_LENGTH = 500;
export const MAX_LENGTH_INPUT_NUMBER = 9;

export const MESSAGE_VALIDATION = {
	END_DATE_MUST_BE_AFTER_START_DATE:
		"開始日は終了日より前の日付を選択してください",
	FILE_TYPE_INVALID: "サポートされていない画像形式です",
	STAMP_IMAGE_SIZE_INVALID:
		"100KB以内で500×500pxの画像をアップロードしてください",
	PLEASE_UPLOAD_FULL_PHOTOS: "全部の写真をアップロードしてください",
	LINE_ID_EXISTED: "自分のトークIDは重複しています",
	LINE_ID_OVER_10: "管理者通知先は最大10件までです。",
};

/* AUTH TYPE */
export const AUTH_MASTER = "master";

export const ROLES = {
	MASTER: "master",
	MANAGER: "manager",
};

/* MESSAGE TYPE */
export const MESSAGE_TYPE_TEXT = "text";

/* SYSTEM TYPE */
export const DEFAULT_SYSTEM_TYPE = "イベント";

/* PAGES */
export const PAGE_ADMIN_INQUIRIES = "お問い合わせ";
export const PAGE_ADMIN_STAMP = "スタンプラリー";
export const PAGE_ADMIN_LOTTERY = "くじ引き";
export const PAGE_ADMIN_COUPONS = "クーポン";
export const PAGE_ADMIN_HOME = "ホーム";
export const PAGE_ADMIN_LIST_EVENTS = "イベント";
export const PAGE_ADMIN_LIST_CAMPAIGN = "キャンペーン";
export const PAGE_ADMIN_OCCASIONS = `${PAGE_ADMIN_LIST_EVENTS}予約管理`;
export const PAGE_ADMIN_CUSTOMERS = "顧客管理";
export const PAGE_ADMIN_MAINTENANCE = "保守サービス管理";
export const PAGE_ADMIN_CAMPAIGN_CUSTOMERS = "キャンペーン管理";
export const PAGE_ADMIN_AUDIENCES = "オーディエンス";
export const PAGE_ADMIN_SETTINGS = "設定";
export const PAGE_ADMIN_SURVEY = "アンケート";
export const PAGE_LINE_OFFICIAL_LOGIN = "LINEマネージャー";
export const PAGE_ADMIN_INFLOW_SOURCE_ANALYSIS = "流入/来店履歴";

/* FULLCALENDAR KEY */
export const FKEY = "0671443620-fcs-1624419667";
export const BUSINESS_OPEN_TIME = "07:00:00";
export const BUSINESS_OPEN_TIME_VALUE = 7;
export const BUSINESS_CLOSE_TIME = "21:00:00";
export const BUSINESS_CLOSE_TIME_VALUE = 21;
export const BUSINESS_INTERVAL_TIME_LABEL = "01:00:00";
export const BUSINESS_INTERVAL_TIME = "01:00:00";
export const BUSINESS_INTERVAL_TIME_VALUE = 60; //MUST BE MINUTE | 15 | 30 | 45 | 60

export const DEFAULT_CLIENT_CATEGORIES_SCREEN_TITLE = `ご希望の${DEFAULT_SYSTEM_TYPE}をお選び下さい`;
export const DEFAULT_CLIENT_CATEGORIES_SCREEN_SUBTITLE =
	"※予約枠が埋まっていてもご相談ください";

/* OCCASION DEFAULT VALUE */
export const DEFAULT_OCCASION_CANCEL_ALLOWED = false;
export const DEFAULT_OCCASION_CANCEL_LIMIT_DAY = 1;
export const DEFAULT_OCCASION_CANCEL_LIMIT_HOUR = 18;
export const DEFAULT_OCCASION_CANCEL_LIMIT_MINUTE = 0;
export const DEFAULT_OCCASION_RESERVATION_LIMIT_ENABLED = false;
export const DEFAULT_OCCASION_RESERVATION_LIMIT_DAY = 1;
export const DEFAULT_OCCASION_RESERVATION_LIMIT_HOUR = 18;
export const DEFAULT_OCCASION_RESERVATION_LIMIT_MINUTE = 0;
export const DEFAULT_OCCURRENCE_MAX_PARTICIPATION = 3;

/* TAB KEY */
export const INQUIRY = "inquiry";
export const INQUIRY_TITLE = "お問い合わせ";

export const OCCURRENCE_WARN_COUNT = 5;

/* Days of week */
export const DAYS_OF_WEEK = [
	{
		label: "日",
		longLabel: "日曜日",
		value: 7,
		times: [
			{
				startTime: dayjs(BUSINESS_OPEN_TIME, "HH:mm:ss"),
				endTime: dayjs(BUSINESS_CLOSE_TIME, "HH:mm:ss"),
			},
		],
		enabled: true,
	},
	{
		label: "月",
		longLabel: "月曜日",
		value: 1,
		times: [
			{
				startTime: dayjs(BUSINESS_OPEN_TIME, "HH:mm:ss"),
				endTime: dayjs(BUSINESS_CLOSE_TIME, "HH:mm:ss"),
			},
		],
		enabled: true,
	},
	{
		label: "火",
		longLabel: "火曜日",
		value: 2,
		times: [
			{
				startTime: dayjs(BUSINESS_OPEN_TIME, "HH:mm:ss"),
				endTime: dayjs(BUSINESS_CLOSE_TIME, "HH:mm:ss"),
			},
		],
		enabled: true,
	},
	{
		label: "水",
		longLabel: "水曜日",
		value: 3,
		times: [
			{
				startTime: dayjs(BUSINESS_OPEN_TIME, "HH:mm:ss"),
				endTime: dayjs(BUSINESS_CLOSE_TIME, "HH:mm:ss"),
			},
		],
		enabled: true,
	},
	{
		label: "木",
		longLabel: "木曜日",
		value: 4,
		times: [
			{
				startTime: dayjs(BUSINESS_OPEN_TIME, "HH:mm:ss"),
				endTime: dayjs(BUSINESS_CLOSE_TIME, "HH:mm:ss"),
			},
		],
		enabled: true,
	},
	{
		label: "金",
		longLabel: "金曜日",
		value: 5,
		times: [
			{
				startTime: dayjs(BUSINESS_OPEN_TIME, "HH:mm:ss"),
				endTime: dayjs(BUSINESS_CLOSE_TIME, "HH:mm:ss"),
			},
		],
		enabled: true,
	},
	{
		label: "土",
		longLabel: "土曜日",
		value: 6,
		times: [
			{
				startTime: dayjs(BUSINESS_OPEN_TIME, "HH:mm:ss"),
				endTime: dayjs(BUSINESS_CLOSE_TIME, "HH:mm:ss"),
			},
		],
		enabled: true,
	},
];

/* FORM SCROLL CONFIG */
export const FORM_SCROLL_CONFIG = {
	behavior: "smooth",
	block: "top",
	inline: "center",
};

/* REGISTRATION VIA */
export const VIA_WEBSITE_VALUE = "website";
export const VIA_WEBSITE_TEXT = "Webサイト";
export const VIA_EXHIBITION_VALUE = "exhibition";
export const VIA_EXHIBITION_TEXT = "展示会";
export const VIA_INTRODUCTION_VALUE = "introduction";
export const VIA_INTRODUCTION_TEXT = "紹介";
export const VIA_OTHERS_VALUE = "others";
export const VIA_OTHERS_TEXT = "その他";
export const VIAS = [
	{ value: VIA_WEBSITE_VALUE, text: VIA_WEBSITE_TEXT },
	{ value: VIA_EXHIBITION_VALUE, text: VIA_EXHIBITION_TEXT },
	{ value: VIA_INTRODUCTION_VALUE, text: VIA_INTRODUCTION_TEXT },
	{ value: VIA_OTHERS_VALUE, text: VIA_OTHERS_TEXT },
];

/* INQUIRY TYPE */
export const INQUIRY_TYPE_SERVICE_VALUE = "service";
export const INQUIRY_TYPE_SERVICE_TEXT = "サービスについて";
export const INQUIRY_TYPE_ACHIEVEMENT_VALUE = "achievement";
export const INQUIRY_TYPE_ACHIEVEMENT_TEXT = "実績について";
export const INQUIRY_TYPE_COMPANY_VALUE = "company";
export const INQUIRY_TYPE_COMPANY_TEXT = "会社について";
export const INQUIRY_TYPE_RECRUIT_VALUE = "recruit";
export const INQUIRY_TYPE_RECRUIT_TEXT = "採用について";
export const INQUIRY_TYPE_OTHERS_VALUE = "others";
export const INQUIRY_TYPE_OTHERS_TEXT = "その他";
export const INQUIRY_TYPES = [
	{ value: INQUIRY_TYPE_SERVICE_VALUE, text: INQUIRY_TYPE_SERVICE_TEXT },
	{
		value: INQUIRY_TYPE_ACHIEVEMENT_VALUE,
		text: INQUIRY_TYPE_ACHIEVEMENT_TEXT,
	},
	{ value: INQUIRY_TYPE_COMPANY_VALUE, text: INQUIRY_TYPE_COMPANY_TEXT },
	{ value: INQUIRY_TYPE_RECRUIT_VALUE, text: INQUIRY_TYPE_RECRUIT_TEXT },
	{ value: INQUIRY_TYPE_OTHERS_VALUE, text: INQUIRY_TYPE_OTHERS_TEXT },
];

/* GET STATUS */
export function GET_STATUS(status) {
	switch (status) {
		case "confirming":
			return "確認中";
		case "handling":
			return "対応中";
		case "completed":
			return "対応完了";
		case "cancelled":
			return "キャンセル";
		default:
			break;
	}
}

/* THEME COLOR */
export const PRIMARY_COLOR = "#99CA29";
export const PRIMARY_LIGHT_COLOR = "#fdfff0";
export const SECONDARY_COLOR = "#21acd7";
export const SECONDARY_LIGHT_COLOR = "#f0feff";
export const THIRD_COLOR = "#fa8c16";
export const THIRD_LIGHT_COLOR = "#fff7e6";
export const CUSTOM_GREEN = "#8ac926";
export const CUSTOM_LIGHT_GREEN = "#fcfff0";
export const CUSTOM_RED = "#ff595e";
export const CUSTOM_LIGHT_RED = "#fff2f0";
export const CUSTOM_YELLOW = "#FAD717";
export const CUSTOM_LIGHT_YELLOW = "#FEFBE8";
export const CUSTOM_GRAY_COLOR = "#d9d9d9";
export const CUSTOM_CAMPAIGN_COLOR = "#960E0E";
export const GRAY_COLOR = "#9CA3AF";
export const WHITE_COLOR = "#FFF";
export const BLACK_COLOR = "#000";
export const SATURDAY_COLOR = "#00c2ff";
export const SUNDAY_COLOR = "#c40055";

export const THEME_COLORS = [
	"#99CA29",
	"#EA638C",
	"#DB5461",
	"#BB3E03",
	"#E85D75",
	"#C76D7E",
	"#AB92BF",
	"#8D6B94",
	"#4EA5D9",
	"#134074",
	"#005F73",
	"#8DA9C4",
	"#72A1E5",
	"#0A9396",
	"#84A07C",
	"#3C787E",
	"#77878B",
	"#9a8c98",
	"#9F8082",
	"#b5838d",
	"#6d6875",
	"#30343F",
	"#2E382E",
];

export const GET_AUDIENCE_STATUS = (value) => {
	switch (value) {
		case "IN_PROGRESS":
			return (
				<Tag color="#fcbf49" className="mr-0">
					作成中
				</Tag>
			);
		case "READY":
			return (
				<Tag color="#06d6a0" className="mr-0">
					完成
				</Tag>
			);
		case "FAILED":
			return (
				<Tag color="#ff6b6b" className="mr-0">
					エラー
				</Tag>
			);
		case "EXPIRED":
			return (
				<Tag color="#f77f00" className="mr-0">
					期限切れ
				</Tag>
			);
		case "INACTIVE":
			return (
				<Tag color="#38a3a5" className="mr-0">
					無効
				</Tag>
			);
		case "ACTIVATING":
			return (
				<Tag color="#52b788" className="mr-0">
					有効化中
				</Tag>
			);
		default:
			break;
	}
};

export const OCCURRENCE_TIME_STYLE = (publicSettings) => {
	return {
		borderColor: publicSettings?.PRIMARY_COLOR,
		backgroundColor: publicSettings?.PRIMARY_LIGHT_COLOR,
		color: publicSettings?.PRIMARY_COLOR,
	};
};

export const RESERVATION_STYLE = (publicSettings) => {
	return {
		borderColor: publicSettings?.PRIMARY_COLOR,
		backgroundColor: publicSettings?.PRIMARY_COLOR,
		color: WHITE_COLOR,
	};
};

export const ZERO_STYLE = (publicSettings) => ({
	borderColor: publicSettings?.PRIMARY_COLOR,
	backgroundColor: "#F2F2F2",
	color: "#A2A2A2",
});

export const ATTENDED_STYLE = (publicSettings) => {
	return {
		borderColor: SECONDARY_COLOR,
		backgroundColor: WHITE_COLOR,
		color: publicSettings?.PRIMARY_COLOR,
	};
};

export const MAX_CAPACITY_STYLE = (publicSettings) => {
	return {
		borderColor: SECONDARY_COLOR,
		backgroundColor: publicSettings?.PRIMARY_LIGHT_COLOR,
		color: publicSettings?.PRIMARY_COLOR,
	};
};

const REGISTRATION_EXPECTED_STYLE = () => {
	return {
		borderColor: SECONDARY_COLOR,
		backgroundColor: SECONDARY_LIGHT_COLOR,
		color: SECONDARY_COLOR,
	};
};

const REGISTRATION_ATTENDED_STYLE = (publicSettings) => {
	return {
		borderColor: publicSettings?.PRIMARY_COLOR,
		backgroundColor: publicSettings?.PRIMARY_LIGHT_COLOR,
		color: publicSettings?.PRIMARY_COLOR,
	};
};

export const CHECK_IS_ATTENDED = (publicSettings, attended) => {
	if ((attended || 0) === 0) {
		return REGISTRATION_EXPECTED_STYLE(publicSettings);
	} else {
		return REGISTRATION_ATTENDED_STYLE(publicSettings);
	}
};

export const IS_ACTIVE = (end) => {
	if (end) {
		return moment().isSameOrBefore(moment(end), "minute");
	} else {
		return false;
	}
};

export const FIND_PERCENTAGE = (x, max) => {
	return Math.round((x / max) * 100);
};

export const OCCURRENCE_STATUS_FULL = "full";
export const OCCURRENCE_STATUS_ALMOST_FULL = "almost-full";
export const OCCURRENCE_STATUS_RESERVED = "reserved";
export const OCCURRENCE_STATUS_AVAILABLE = "available";

export const CHECK_IS_EVENT_FULL = (max, current) => {
	return max > 0 && current > 0 && max === current;
};

export const GET_LEFT_SLOTS = (max, current) => {
	return max - current;
};

export const IS_ALREADY_RESERVED = (registrations, occurrenceId) => {
	if (registrations && registrations.length > 0) {
		const isReserved = registrations.filter((registration) => {
			if (registration?.occurrences) {
				return registration.occurrences.find(
					(ro) => ro?.occurrenceId === occurrenceId,
				);
			} else {
				return false;
			}
		});

		return isReserved.length > 0 ? true : false;
	} else {
		return false;
	}
};

export const CAN_RESERVE = (
	slotCount,
	registrations,
	occurrenceId,
	max,
	current,
) => {
	if (IS_ALREADY_RESERVED(registrations, occurrenceId)) {
		return false;
	}

	if (CHECK_IS_EVENT_FULL(max, current)) {
		return false;
	}

	if (GET_LEFT_SLOTS(max, current) < slotCount) {
		return false;
	}

	return true;
};

export const ARRAY_RANGE_CREATOR = (start, end, step) => {
	return Array(Math.ceil((end - start) / step))
		.fill(start)
		.map((x, y) => x + y * step);
};

export function USE_IS_MOUNTED_REF() {
	const isMountedRef = useRef(null);

	useEffect(() => {
		isMountedRef.current = true;
		return () => (isMountedRef.current = false);
	});

	return isMountedRef;
}

export function COLOR_ADJUST(color, amount) {
	const colors = generate(color);

	return colors[amount - 100] || colors[0];
}

export const RE_ORDER = (list, startIndex, endIndex) => {
	const result = Array.from(list);
	const [removed] = result.splice(startIndex, 1);
	result.splice(endIndex, 0, removed);

	return result;
};

export function POSTAL_CODE_INSERT_CHARACTER(str, index, value) {
	return str.substr(0, index) + value + str.substr(index);
}

export const RESIZE_FILE = (file, type) =>
	new Promise((resolve) => {
		Resizer.imageFileResizer(
			file,
			1024,
			1024,
			"JPEG",
			80,
			0,
			(uri) => {
				resolve(uri);
			},
			type,
		);
	});

export const SESSION_STORAGE_USER_INFO_KEY = "PREGIO:USER_INFO";

const GET_SESSION_STORAGE_OR_DEFAULT = (key, defaultValue) => {
	const stored = sessionStorage.getItem(key);
	if (!stored) {
		return defaultValue;
	}
	return JSON.parse(stored);
};

export const USE_SESSION_STORAGE = (key, defaultValue) => {
	const [value, setValue] = useState(
		GET_SESSION_STORAGE_OR_DEFAULT(key, defaultValue),
	);

	useEffect(() => {
		sessionStorage.setItem(key, JSON.stringify(value));
	}, [key, value]);

	return [value, setValue];
};

const GET_LOCAL_STORAGE_OR_DEFAULT = (key, defaultValue) => {
	const stored = localStorage.getItem(key);
	if (!stored) {
		return defaultValue;
	}
	return JSON.parse(stored);
};

export const USE_LOCAL_STORAGE = (key, defaultValue) => {
	const [value, setValue] = useState(
		GET_LOCAL_STORAGE_OR_DEFAULT(key, defaultValue),
	);

	useEffect(() => {
		localStorage.setItem(key, JSON.stringify(value));
	}, [key, value]);

	return [value, setValue];
};

export const ANIMATION_VARIANT_STAGGER_CONTAINER = {
	hidden: {
		transition: {
			staggerChildren: 0.01,
			staggerDirection: -1,
			when: "afterChildren",
		},
	},
	show: {
		transition: {
			staggerChildren: 0.04,
			staggerDirection: 1,
			when: "beforeChildren",
		},
	},
};

export const ANIMATION_VARIANT_STAGGER_ITEM = {
	hidden: {
		opacity: 0,
		// scale: 1,
		transition: { type: "linear" },
	},
	show: {
		opacity: 1,
		// scale: [0.95, 1],
		transition: { type: "linear" },
	},
};

export const PREFECTURES = [
	{ value: 1, label: "北海道" },
	{ value: 2, label: "青森県" },
	{ value: 3, label: "岩手県" },
	{ value: 4, label: "宮城県" },
	{ value: 5, label: "秋田県" },
	{ value: 6, label: "山形県" },
	{ value: 7, label: "福島県" },
	{ value: 8, label: "茨城県" },
	{ value: 9, label: "栃木県" },
	{ value: 10, label: "群馬県" },
	{ value: 11, label: "埼玉県" },
	{ value: 12, label: "千葉県" },
	{ value: 13, label: "東京都" },
	{ value: 14, label: "神奈川県" },
	{ value: 15, label: "新潟県" },
	{ value: 16, label: "富山県" },
	{ value: 17, label: "石川県" },
	{ value: 18, label: "福井県" },
	{ value: 19, label: "山梨県" },
	{ value: 20, label: "長野県" },
	{ value: 21, label: "岐阜県" },
	{ value: 22, label: "静岡県" },
	{ value: 23, label: "愛知県" },
	{ value: 24, label: "三重県" },
	{ value: 25, label: "滋賀県" },
	{ value: 26, label: "京都府" },
	{ value: 27, label: "大阪府" },
	{ value: 28, label: "兵庫県" },
	{ value: 29, label: "奈良県" },
	{ value: 30, label: "和歌山県" },
	{ value: 31, label: "鳥取県" },
	{ value: 32, label: "島根県" },
	{ value: 33, label: "岡山県" },
	{ value: 34, label: "広島県" },
	{ value: 35, label: "山口県" },
	{ value: 36, label: "徳島県" },
	{ value: 37, label: "香川県" },
	{ value: 38, label: "愛媛県" },
	{ value: 39, label: "高知県" },
	{ value: 40, label: "福岡県" },
	{ value: 41, label: "佐賀県" },
	{ value: 42, label: "長崎県" },
	{ value: 43, label: "熊本県" },
	{ value: 44, label: "大分県" },
	{ value: 45, label: "宮崎県" },
	{ value: 46, label: "鹿児島県" },
	{ value: 47, label: "沖縄県" },
];

export const SURVEY_QUESTION_TYPES = {
	TEXT: "text",
	DATE_PICKER: "datepicker",
	RADIO: "radio",
	CHECKBOX: "checkbox",
	IMAGE: "image",
	NOTE: "note",
	// SELECT: "select",
	// DATE: "date",
	// TIME: "time",
	// DATETIME: "datetime",
	// FILE: "file",
	// NUMBER: "number",
	// EMAIL: "email",
	// TEL: "tel",
	// URL: "url",
};

export const SURVEY_QUESTION_TYPE_LABELS = {
	text: "テキスト入力",
	datepicker: "日付入力",
	radio: "単一選択",
	checkbox: "複数選択",
	image: "写真",
	note: "なし",
};

/**
 * @param {string} str
 * @returns {string}
 */
export const halfWidthToFullWidth = (str) => {
	return str.replace(/[\u0021-\u007E]/g, function (char) {
		return String.fromCharCode(char.charCodeAt(0) + 0xfee0);
	});
};

export const TIME_FORMAT = "HH:mm";

export const JP_YEAR_FORMAT = "YYYY年";
export const JP_MONTH_FORMAT = "MM月";
export const JP_DAY_FORMAT = "DD日";
export const JP_MONTH_DAY_FORMAT = "MM月DD日";
export const JP_DATE_FORMAT = "YYYY年MM月DD日";
export const JP_TIME_FORMAT = "HH時mm分";
export const JP_DATE_TIME_FORMAT = `${JP_DATE_FORMAT} ${JP_TIME_FORMAT}`;
export const DATE_RAW_FORMAT = "YYYYMMDD";

export function GET_BASE_64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = (error) => reject(error);
	});
}

export const AUDIENCE_LOTTERY_SEARCH_STATUS_ALL = "all";
export const AUDIENCE_LOTTERY_SEARCH__STATUS_IS_WIN = "isWin";
export const SORT_DIRECTIONS_COMMON = ["ascend", "descend"];
export const AUDIENCE_LOTTERY_FORMAT_DATE = ["ascend", "descend"];

export const LOCAL_STORAGE_KEYS = {
	CAMPAIGN: "campaign",
	RECORD: "record",
	CAMPAIGN_REGISTRATIONS: "campaign-registrations",
	CLIENT_LOTTERY_ID: "client-lottery-id",
	CLIENT_SURVEY_RECORD_ID: "client-survey-record-id",
	CLIENT_ACCESS_LAST_PAGE: "client-access-last-page",
	STAMP_IMAGE_ID: "stamp-image-id",
	CATEGORY: "is-category",
	CATEGORY_ID: "category-id",
	IS_PROGRAM: "is-program",
	INFLOW_SOURCE_ID: "inflow-source-id",
};

export const LOCAL_STORAGE_KEYS_REDIRECT = {
	[LOCAL_STORAGE_KEYS.CLIENT_LOTTERY_ID]: LOCAL_STORAGE_KEYS.CLIENT_LOTTERY_ID,
	[LOCAL_STORAGE_KEYS.CLIENT_SURVEY_RECORD_ID]:
		LOCAL_STORAGE_KEYS.CLIENT_SURVEY_RECORD_ID,
	[LOCAL_STORAGE_KEYS.CLIENT_ACCESS_LAST_PAGE]:
		LOCAL_STORAGE_KEYS.CLIENT_ACCESS_LAST_PAGE,
};

export const getLiffAccessToken = () => liff.getAccessToken();

export const REGEX_ZIPCODE_INPUT = /[^0-9-]/g;
export const REGEX_ZIPCODE_INPUT_CHECK = /^\d+(-\d+)?$/;
export const REGEX_REMOVE_NON_DIGIT = /[^\d]/g;

export const RICH_MENU_TYPE = {
	DEFAULT: "defaultRM",
	MEMBER: "memberRM",
};

export const getRichMenuCompactImageArea = (width = 1200, height = 405) => ({
	"richmenu-template-guidem-01.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width / 3,
					height: height,
				},
			},
			{
				bounds: {
					x: width / 3,
					y: 0,
					width: width / 3,
					height: height,
				},
			},
			{
				bounds: {
					x: (width / 3) * 2,
					y: 0,
					width: width / 3,
					height: height,
				},
			},
		],
	},
	"richmenu-template-guidem-02.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width / 2,
					height: height,
				},
			},
			{
				bounds: {
					x: width / 2,
					y: 0,
					width: width / 2,
					height: height,
				},
			},
		],
	},
	"richmenu-template-guidem-03.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: (width / 3) * 2,
					height: height,
				},
			},
			{
				bounds: {
					x: (width / 3) * 2,
					y: 0,
					width: width / 3,
					height: height,
				},
			},
		],
	},
	"richmenu-template-guidem-04.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width / 3,
					height: height,
				},
			},
			{
				bounds: {
					x: width / 3,
					y: 0,
					width: (width / 3) * 2,
					height: height,
				},
			},
		],
	},
	"richmenu-template-guidem-05.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width,
					height: height,
				},
			},
		],
	},
});

export const getRichMenuLargeImageArea = (width = 1200, height = 810) => ({
	"richmenu-template-guidem-01.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width / 3,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: width / 3,
					y: 0,
					width: width / 3,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: (width / 3) * 2,
					y: 0,
					width: width / 3,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: 0,
					y: height / 2,
					width: width / 3,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: width / 3,
					y: height / 2,
					width: width / 3,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: (width / 3) * 2,
					y: height / 2,
					width: width / 3,
					height: height / 2,
				},
			},
		],
	},
	"richmenu-template-guidem-02.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width / 2,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: width / 2,
					y: 0,
					width: width / 2,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: 0,
					y: height / 2,
					width: width / 2,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: width / 2,
					y: height / 2,
					width: width / 2,
					height: height / 2,
				},
			},
		],
	},
	"richmenu-template-guidem-03.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: 0,
					y: height / 2,
					width: width / 3,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: width / 3,
					y: height / 2,
					width: width / 3,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: (width / 3) * 2,
					y: height / 2,
					width: width / 3,
					height: height / 2,
				},
			},
		],
	},
	"richmenu-template-guidem-04.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: (width / 3) * 2,
					height: height,
				},
			},
			{
				bounds: {
					x: (width / 3) * 2,
					y: 0,
					width: width / 3,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: (width / 3) * 2,
					y: height / 2,
					width: width / 3,
					height: height / 2,
				},
			},
		],
	},
	"richmenu-template-guidem-05.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width,
					height: height / 2,
				},
			},
			{
				bounds: {
					x: 0,
					y: height / 2,
					width: width,
					height: height / 2,
				},
			},
		],
	},
	"richmenu-template-guidem-06.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width / 2,
					height: height,
				},
			},
			{
				bounds: {
					x: width / 2,
					y: 0,
					width: width / 2,
					height: height,
				},
			},
		],
	},
	"richmenu-template-guidem-07.png": {
		areas: [
			{
				bounds: {
					x: 0,
					y: 0,
					width: width,
					height: height,
				},
			},
		],
	},
});

export const getRichMenuTabbedCompactImageArea = (width = 1200, height = 405) => {
	const TAB_HEIGHT = 110;
	const CONTENT_HEIGHT = height - TAB_HEIGHT;

	return {
		"richmenu-template-guidem-01.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT,
					},
				},
			],
		},
		"richmenu-template-guidem-02.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width / 2,
						height: CONTENT_HEIGHT,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: TAB_HEIGHT,
						width: width / 2,
						height: CONTENT_HEIGHT,
					},
				},
			],
		},
		"richmenu-template-guidem-03.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: (width / 3) * 2,
						height: CONTENT_HEIGHT,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT,
					},
				},
			],
		},
		"richmenu-template-guidem-04.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: TAB_HEIGHT,
						width: (width / 3) * 2,
						height: CONTENT_HEIGHT,
					},
				},
			],
		},
		"richmenu-template-guidem-05.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width,
						height: CONTENT_HEIGHT,
					},
				},
			],
		},
	};
};

export const getRichMenuTabbedImageArea = (width = 1200, height = 810) => {
	const TAB_HEIGHT = 110;
	const CONTENT_HEIGHT = height - TAB_HEIGHT;

	return {
		"richmenu-template-guidem-01.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-02.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width / 2,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: TAB_HEIGHT,
						width: width / 2,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 2,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 2,
						height: CONTENT_HEIGHT / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-03.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-04.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: (width / 3) * 2,
						height: CONTENT_HEIGHT,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: TAB_HEIGHT,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width / 3,
						height: CONTENT_HEIGHT / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-05.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width,
						height: CONTENT_HEIGHT / 2,
					},
				},
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT + CONTENT_HEIGHT / 2,
						width: width,
						height: CONTENT_HEIGHT / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-06.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width / 2,
						height: CONTENT_HEIGHT,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: TAB_HEIGHT,
						width: width / 2,
						height: CONTENT_HEIGHT,
					},
				},
			],
		},
		"richmenu-template-guidem-07.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: TAB_HEIGHT,
						width: width,
						height: CONTENT_HEIGHT,
					},
				},
			],
		},
	};
};

export const RICH_MENU_LARGE_SIZE = [1200, 810];
export const RICH_MENU_COMPACT_SIZE = [1200, 405];

export const RICH_MENU_ACTION_TYPE = {
	TEL: "tel",
	URI: "uri",
	MESSAGE: "message",
	MEMBERSHIP: "membership",
};

export const LIFF_URI = `https://liff.line.me/${import.meta.env.VITE_APP_LINE_LIFF_ID}`;

export const RICH_MENU_PLACEHOLDER_BY_ACTION_TYPE = {
	[RICH_MENU_ACTION_TYPE.TEL]:
		"半角で入力をお願いします。（例：090-1234-5678、09012345678）",
	[RICH_MENU_ACTION_TYPE.URI]: "例：https://example.com",
	[RICH_MENU_ACTION_TYPE.MESSAGE]: "例：メッセージ",
	[RICH_MENU_ACTION_TYPE.MEMBERSHIP]: `${LIFF_URI}`,
};

export const RICH_MENU_ACTION_TYPE_LABEL = {
	[RICH_MENU_ACTION_TYPE.TEL]: "電話番号",
	[RICH_MENU_ACTION_TYPE.URI]: "リンク",
	[RICH_MENU_ACTION_TYPE.MESSAGE]: "メッセージ",
	[RICH_MENU_ACTION_TYPE.MEMBERSHIP]: "会員証リンク",
};

export const RICH_MENU_ACTION_TYPE_OPTIONS = [
	{
		label: "電話番号",
		value: RICH_MENU_ACTION_TYPE.TEL,
	},
	{
		label: "リンク",
		value: RICH_MENU_ACTION_TYPE.URI,
	},
	{
		label: "メッセージ",
		value: RICH_MENU_ACTION_TYPE.MESSAGE,
	},
	{
		label: "会員証リンク",
		value: RICH_MENU_ACTION_TYPE.MEMBERSHIP,
	},
];

export const JAPAN_PHONE_PATTERN = /^[0-9-]{10,14}$/;

export const isValidJapanesePhoneNumber = (str) => {
	if (!JAPAN_PHONE_PATTERN.test(str)) {
		return false;
	}

	const digitCount = str.replace(/-/g, "").length;
	return digitCount >= 10;
};

export const isURL = (str) => {
	const urlRegex =
		"^(?!mailto:)(?:(?:http|https|ftp)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$";
	const url = new RegExp(urlRegex, "i");
	return str.length < 2083 && url.test(str);
};

export const getImageSize = (file) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const img = new Image();
			img.onload = () => {
				resolve([img.width, img.height]);
			};
			img.onerror = () => {
				message.error("Invalid image file");
				reject(new Error("Invalid image file"));
			};
			img.src = e.target.result;
		};
		reader.onerror = () => {
			message.error("Failed to read file");
			reject(new Error("Failed to read file"));
		};
		reader.readAsDataURL(file);
	});
};

export const getRichMenuPositionActionByIndex = (index) =>
	String.fromCharCode(65 + index);

export const TITLE_SURVEY_NOT_FOUND = "アンケートが見つかりません";
export const TITLE_SURVEY_DELETED = "このアンケートは\n既に削除されています";
export const TITLE_SURVEY_IMAGE_SIZE = "ファイルサイズ：5MB以下";
export const TITLE_SURVEY_EXPIRED = "アンケートの有効期限が\n切れています。";
export const TITLE_SURVEY_EMPTY = "現在、アンケートには質問がありません";
export const TITLE_SURVEY_DEFAULT = "以下のフォームにご記入ください";

export const SYSTEM_SETTING_NAMES = {
	IS_EVENT_BOOKING_TAB_VISIBLE: "isEventBookingTabVisible",
	STORE_PIC: "storePic",
	IS_BARCODE_ENABLED: "isBarcodeEnabled",
	IS_POINT_ENABLED: "isPointEnabled",
	IS_DATE_OF_EXPIRY_ENABLED: "isDateOfExpiryEnabled",
};

export const ROUTES = {
	// ADMIN

	// CLIENT
	CLIENT_LIST_CATEGORY: "liff/categories/:liffId",
	CLIENT_DETAIL_CATEGORY: "liff/categories/:liffId/:categoryId",
	CLIENT_LIST_CAMPAIGN: "liff/categories/:liffId",
	CLIENT_DETAIL_CAMPAIGN: "liff/categories/:liffId/:campaignId",
	CLIENT_LIST_REGISTRATIONS: "liff/registrations/:liffId",
	CLIENT_DETAIL_MEMBERSHIP: "liff/membership/:liffId",
};

export const ACTION_CANCEL_EVENT = "当日キャンセル";
export const TITLE_ACTION_CANCEL_EVENT = "キャンセル";

export const CUSTOMER_REGISTRATION_FIELD_TYPE = {
	TEXT: "text",
	NUMBER: "number",
	DATE_PICKER: "datepicker",
	RADIO: "radio",
	CHECKBOX: "checkbox",
	SELECT: "select",
	MULTISELECT: "multiselect",
	IMAGE: "image",
};

export const SOCKET_EVENTS = {
	MEMBER_CREATED: "member:created",
	MEMBER_UPDATED: "member:updated",
	MEMBER_DELETED: "member:deleted",

	CHAT_SEEN: "chat:seen",
	CHAT_CREATED: "chat:created",

	AUDIENCE_CREATED: "audience:created",

	CUSTOMER_REGISTRATIONS_CHANGED: "customer_registrations:changed",
};

export const SURVEY_PAGE_TYPE = {
	SINGLE_PAGE: "single",
	MULTIPLE_PAGE: "multiple",
};

export const OPTION_SURVEY_PAGE_LAYOUT = [
	{ label: "1ページ", value: SURVEY_PAGE_TYPE.SINGLE_PAGE },
	{ label: "1質問/1ページ", value: SURVEY_PAGE_TYPE.MULTIPLE_PAGE },
];

export const SURVEY_PAGE_LAYOUT_LABEL = {
	[SURVEY_PAGE_TYPE.SINGLE_PAGE]: "1ページ",
	[SURVEY_PAGE_TYPE.MULTIPLE_PAGE]: "1質問/1ページ",
};

export const AUDIENCE_TYPE = {
	DEFAULT: "default",
	CAMPAIGN: "campaign",
	SURVEY: "survey",
	LOTTERY: "lottery",
	MEMBER: "member",
	VISIT: "visit",
	SUBSCRIPTION: "subscription",
};

export const FILTER_LABELS = {
	friendAddedDateMin: "友だち追加日から",
	friendAddedDateMax: "友だち追加日まで",
	memberSinceMin: "会員登録日から",
	memberSinceMax: "会員登録日まで",
	lastVisitMin: "最終来店日から",
	lastVisitMax: "最終来店日まで",
	isFriends: {
		label: "ブロック状態",
		false: { label: "ブロックユーザーのみ", value: false },
		true: { label: "ブロックユーザーを含まない", value: true },
	},
	isRegistered: {
		label: "会員登録状態",
		true: { label: "会員登録あり", value: true },
		false: { label: "会員登録なし", value: false },
	},
	messages: {
		label: "LINEチャット",
		unread: {
			label: "未読",
			value: "unread",
		},
		read: {
			label: "既読",
			value: "read",
		},
	},
};

export const AUDIENCE_STATUS = {
	IN_PROGRESS: "IN_PROGRESS",
	READY: "READY",
};

export const MEMBER_ATTENDED_TAB_ACTIVE_KEY = {
	EVENT: "event",
	SURVEY_ANSWERS: "surveyAnswers",
	LOTTERY: "lottery",
	VISIT: "visit",
};

export const SURVEY_NAVIGATION_TYPE = {
	NEXT_QUESTION: "next_question",
	CONDITIONAL_BRANCHING: "conditional_branching",
	NEXT_SPECIFIED_QUESTION: "next_specified_question",
};

export const NAVIGATION_TYPE_OPTIONS = [
	{ label: "次の質問に進む", value: SURVEY_NAVIGATION_TYPE.NEXT_QUESTION },
	{
		label: "遷移先を指定",
		value: SURVEY_NAVIGATION_TYPE.NEXT_SPECIFIED_QUESTION,
	},
	{
		label: "回答に応じて分岐",
		value: SURVEY_NAVIGATION_TYPE.CONDITIONAL_BRANCHING,
	},
];

export const OPTION_PAGE_ADMIN_SETTINGS_LABEL = {
	SYSTEM: "システム設定",
	EVENT: "イベント設定",
	NOTIFICATION: "通知",
	RICHMENU: "リッチメニュー",
	MEMBERSHIP_CARD: "会員証項目",
};

export const getNameClientFormSurvey = (tpId) => {
	return `tpId${tpId}`;
};

export const genValueRadioFormSurvey = ({ contents, idx }) => {
	return `${contents}-${idx}`;
};

export const getValueRadioFormSurvey = (data) => {
	try {
		if (typeof data !== "string") {
			return { index: undefined, value: data };
		}

		const lastDashIndex = data.lastIndexOf("-");

		if (lastDashIndex !== -1) {
			const value = data.substring(0, lastDashIndex);
			const indexStr = data.substring(lastDashIndex + 1);
			const index = Number(indexStr);

			if (!Number.isNaN(index)) {
				return { value, index };
			}
		}

		// fallback (text input or invalid format)
		return { value: data, index: undefined };
	} catch (error) {
		if (import.meta.env.VITE_APP_ENV !== "production") {
			console.warn("[getValueRadioFormSurvey] invalid radio payload");
		}
		return { index: undefined, value: data };
	}
};

export const SYSTEM_SETTINGS_LABEL = {
	IS_BARCODE_ENABLED: "バーコード",
	IS_POINT_ENABLED: "ポイント",
	IS_DATE_OF_EXPIRY_ENABLED: "有効期限",
};

export const PUBLIC_SETTINGS_KEY_BARCODE_ENABLED = "isBarcodeEnabled";
export const PUBLIC_SETTINGS_KEY_POINT_ENABLED = "isPointEnabled";
export const PUBLIC_SETTINGS_KEY_DATE_OF_EXPIRY_ENABLED =
	"isDateOfExpiryEnabled";

export const transformPublicSettings = (data) => {
	return {
		PRIMARY_COLOR: data[API.SETTINGS_KEY_SYSTEM_COLOR]
			? data[API.SETTINGS_KEY_SYSTEM_COLOR].valueString
			: PRIMARY_COLOR,
		PRIMARY_LIGHT_COLOR: data[API.SETTINGS_KEY_SYSTEM_COLOR]
			? COLOR_ADJUST(data[API.SETTINGS_KEY_SYSTEM_COLOR].valueString, 100)
			: PRIMARY_LIGHT_COLOR,
		TITLE: data[API.SETTINGS_KEY_SYSTEM_TITLE]
			? data[API.SETTINGS_KEY_SYSTEM_TITLE].valueString
			: import.meta.env.VITE_APP_SYSTEM_NAME,
		ADMIN_INITIAL_RESERVABLE_COUNT: data[
			API.SETTINGS_KEY_ADMIN_INITIAL_RESERVABLE_COUNT
		]
			? data[API.SETTINGS_KEY_ADMIN_INITIAL_RESERVABLE_COUNT].valueNumber
			: DEFAULT_OCCURRENCE_MAX_PARTICIPATION,
		CLIENT_CATEGORIES_SCREEN_TITLE: data[
			API.SETTINGS_KEY_CLIENT_CATEGORIES_SCREEN_TITLE
		]
			? data[API.SETTINGS_KEY_CLIENT_CATEGORIES_SCREEN_TITLE].valueString
			: DEFAULT_CLIENT_CATEGORIES_SCREEN_TITLE,
		CLIENT_CATEGORIES_SCREEN_SUBTITLE: data[
			API.SETTINGS_KEY_CLIENT_CATEGORIES_SCREEN_SUBTITLE
		]
			? data[API.SETTINGS_KEY_CLIENT_CATEGORIES_SCREEN_SUBTITLE].valueString
			: DEFAULT_CLIENT_CATEGORIES_SCREEN_SUBTITLE,
		RESERVATION_CONFIRM_URL: data[API.SETTINGS_KEY_RESERVATION_CONFIRM_URL]
			? data[API.SETTINGS_KEY_RESERVATION_CONFIRM_URL].valueString
			: undefined,
		COMPANY_TELEPHONE: data[API.SETTINGS_KEY_COMPANY_TELEPHONE]
			? data[API.SETTINGS_KEY_COMPANY_TELEPHONE].valueString
			: undefined,
		CANCEL_ALLOWED: data[API.SETTINGS_KEY_BOOK_CANCEL_ALLOWED]
			? data[API.SETTINGS_KEY_BOOK_CANCEL_ALLOWED].valueFlag
			: DEFAULT_OCCASION_CANCEL_ALLOWED,
		CANCEL_LIMIT_DAY: data[API.SETTINGS_KEY_BOOK_CANCEL_LIMIT_DAY]
			? data[API.SETTINGS_KEY_BOOK_CANCEL_LIMIT_DAY].valueNumber
			: DEFAULT_OCCASION_CANCEL_LIMIT_DAY,
		CANCEL_LIMIT_HOUR: data[API.SETTINGS_KEY_BOOK_CANCEL_LIMIT_HOUR]
			? data[API.SETTINGS_KEY_BOOK_CANCEL_LIMIT_HOUR].valueNumber
			: DEFAULT_OCCASION_CANCEL_LIMIT_HOUR,
		CANCEL_LIMIT_MINUTE: data[API.SETTINGS_KEY_BOOK_CANCEL_LIMIT_MINUTE]
			? data[API.SETTINGS_KEY_BOOK_CANCEL_LIMIT_MINUTE].valueNumber
			: DEFAULT_OCCASION_CANCEL_LIMIT_MINUTE,
		CANCEL_TEXT: data[API.SETTINGS_KEY_BOOK_CANCEL_TEXT]
			? data[API.SETTINGS_KEY_BOOK_CANCEL_TEXT].valueString
			: undefined,
		RESERVATION_LIMIT_ENABLED: data[API.SETTINGS_KEY_BOOK_LIMIT_ENABLED]
			? data[API.SETTINGS_KEY_BOOK_LIMIT_ENABLED].valueFlag
			: DEFAULT_OCCASION_RESERVATION_LIMIT_ENABLED,
		RESERVATION_LIMIT_DAY: data[API.SETTINGS_KEY_BOOK_LIMIT_DAY]
			? data[API.SETTINGS_KEY_BOOK_LIMIT_DAY].valueNumber
			: DEFAULT_OCCASION_RESERVATION_LIMIT_DAY,
		RESERVATION_LIMIT_HOUR: data[API.SETTINGS_KEY_BOOK_LIMIT_HOUR]
			? data[API.SETTINGS_KEY_BOOK_LIMIT_HOUR].valueNumber
			: DEFAULT_OCCASION_RESERVATION_LIMIT_HOUR,
		RESERVATION_LIMIT_MINUTE: data[API.SETTINGS_KEY_BOOK_LIMIT_MINUTE]
			? data[API.SETTINGS_KEY_BOOK_LIMIT_MINUTE].valueNumber
			: DEFAULT_OCCASION_RESERVATION_LIMIT_MINUTE,
		MEMBER_MESSAGE_RESERVATION: data[
			API.SETTINGS_KEY_MEMBER_MESSAGE_RESERVATION
		]
			? data[API.SETTINGS_KEY_MEMBER_MESSAGE_RESERVATION].valueString
			: undefined,
		MEMBER_MESSAGE_CAMPAIGN: data[API.SETTINGS_KEY_MEMBER_MESSAGE_CAMPAIGN]
			? data[API.SETTINGS_KEY_MEMBER_MESSAGE_CAMPAIGN].valueString
			: undefined,
		MEMBER_MESSAGE_REMIND1: data[API.SETTINGS_KEY_MEMBER_MESSAGE_REMIND1]
			? data[API.SETTINGS_KEY_MEMBER_MESSAGE_REMIND1].valueString
			: undefined,
		MEMBER_MESSAGE_REMIND2: data[API.SETTINGS_KEY_MEMBER_MESSAGE_REMIND2]
			? data[API.SETTINGS_KEY_MEMBER_MESSAGE_REMIND2].valueString
			: undefined,
		MEMBER_WINNING_MESSAGE: data[API.SETTINGS_KEY_MEMBER_WINNING_MESSAGE]
			? data[API.SETTINGS_KEY_MEMBER_WINNING_MESSAGE].valueString
			: undefined,
		ADMIN_MESSAGE_MEMBER: data[API.SETTINGS_KEY_ADMIN_MESSAGE_MEMBER]
			? data[API.SETTINGS_KEY_ADMIN_MESSAGE_MEMBER].valueString
			: undefined,
		ADMIN_MESSAGE_CAMPAIGN: data[API.SETTINGS_KEY_ADMIN_MESSAGE_CAMPAIGN]
			? data[API.SETTINGS_KEY_ADMIN_MESSAGE_CAMPAIGN].valueString
			: undefined,
		ADMIN_MESSAGE_RESERVATION: data[API.SETTINGS_KEY_ADMIN_MESSAGE_RESERVATION]
			? data[API.SETTINGS_KEY_ADMIN_MESSAGE_RESERVATION].valueString
			: undefined,
		ADMIN_MESSAGE_RESERVATION_CANCEL: data[
			API.SETTINGS_KEY_ADMIN_MESSAGE_RESERVATION_CANCEL
		]
			? data[API.SETTINGS_KEY_ADMIN_MESSAGE_RESERVATION_CANCEL].valueString
			: undefined,
		BARCODE_ENABLED: data[API.SETTINGS_KEY_BARCODE_ENABLED]
			? data[API.SETTINGS_KEY_BARCODE_ENABLED].valueFlag
			: false,
		POINTS_ENABLED: data[API.SETTINGS_KEY_POINT_ENABLED]
			? data[API.SETTINGS_KEY_POINT_ENABLED].valueFlag
			: false,
		DATE_OF_EXPIRY_ENABLED: data[API.SETTINGS_KEY_DATE_OF_EXPIRY_ENABLED]
			? data[API.SETTINGS_KEY_DATE_OF_EXPIRY_ENABLED].valueFlag
			: false,
		SUBSCRIPTION_POINT_ENABLED: data[API.SETTINGS_KEY_SUBSCRIPTION_POINT_ENABLED]
			? data[API.SETTINGS_KEY_SUBSCRIPTION_POINT_ENABLED].valueFlag
			: false,
		POINT_GRANT_RATE: data[API.SETTINGS_KEY_POINT_GRANT_RATE]
			? data[API.SETTINGS_KEY_POINT_GRANT_RATE].valueNumber
			: 0,
		POINT_CONVERSION_RATE: data[API.SETTINGS_KEY_POINT_CONVERSION_RATE]
			? data[API.SETTINGS_KEY_POINT_CONVERSION_RATE].valueNumber
			: 1,
	};
};

export const DATE_FORMAT_TYPE = {
	DATE_JP: "YYYY年M月D日",
};

export const formatDate = (value, options = {}) => {
	const formatType = options?.formatType || DATE_FORMAT_TYPE.DATE_JP;
	const defaultValue = options?.defaultValue || "ー";

	return value ? moment(value).format(formatType) : defaultValue;
};

export const isNumeric = (str) => {
	return /^\d+$/.test(str);
};

export const SETTINGS_CUSTOMER_REGISTRATION_TYPE = {
	isAdminDisplayed: "isAdminDisplayed",
	isDisplayed: "isDisplayed",
};

export const CONTENT_TYPE_SURVEY_OTHER = "その他（テキスト入力）";

export const genNameClientFormSurveyForTypeOther = (tpId) => {
	const nameForm = getNameClientFormSurvey(tpId);
	return `${nameForm}_other`;
};

export const isTrue = (value) => {
	if (typeof value === "string") {
		return value?.toLocaleLowerCase() === "true";
	}
	return value === 1 || value === true;
};

export const processSearch = (values) => {
	let valuesOmitted = omitBy(values, (val) => isNil(val) || val === "");

	return Object.keys(valuesOmitted).reduce((prev, key) => {
		let value = valuesOmitted[key];
		
		const isDateString = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
		
		if (SEARCH_DATE_TIME_MIN.includes(key) || (key.endsWith("Min") && isDateString)) {
			value = `${value} 00:00:00`;
		}
		if (SEARCH_DATE_TIME_MAX.includes(key) || (key.endsWith("Max") && isDateString)) {
			value = `${value} 23:59:59`;
		}

		return {
			...prev,
			[key]: value,
		};
	}, {});
};

export const checkCampaignChoicesNotDeleteChanged = ({
	newChoices,
	oldChoices,
}) => {
	if (
		!Array.isArray(newChoices) ||
		!Array.isArray(oldChoices) ||
		newChoices.length === 0 ||
		oldChoices.length === 0
	) {
		return false;
	}

	const isDelete = oldChoices?.some((it) => it.isDelete === false);

	if (!isDelete) return false;

	const oldChoicesNotDelete = oldChoices.filter(
		(oldChoice) => oldChoice.isDelete === false,
	);
	const newChoicesNotDelete = newChoices.filter(
		(oldChoice) => oldChoice.isDelete === false,
	);

	return !isEmpty(
		differenceWith(
			newChoicesNotDelete,
			oldChoicesNotDelete,
			(newItem, oldItem) =>
				newItem.campaignChoiceId === oldItem.campaignChoiceId &&
				newItem.contents === oldItem.contents,
		),
	);
};

export const OPTION_SUBMIT_NAVIGATE_SURVEY = {
	label: "フォーム送信",
	tpId: -1,
};

export const LABEL_NAME_LAST_NAME = "名前（苗字）";

export const getNameForDeleteMember = ({ member, customerRegistrations }) => {
	const customerRegisterFullName = customerRegistrations.find(
		(it) => it.label === LABEL_NAME_LAST_NAME,
	);

	let dataName =
		member?.[
			`customerRegistrationId${customerRegisterFullName?.customerRegistrationId}`
		];

	if (!dataName) {
		dataName = member?.displayName;
	}
	return dataName;
};

export const validateEndDateAfterStartDate = ({
	getFieldValue,
	fieldName,
	errorMsgRequired = "",
	errorMsgInvalidRange,
	allowEqual = false,
}) => ({
	validator(_, value) {
		const startDate = getFieldValue(fieldName);
		if (!value && errorMsgRequired) {
			return Promise.reject(new Error(errorMsgRequired));
		}

		if (startDate) {
			const isInvalid = allowEqual ? value < startDate : value <= startDate;
			if (isInvalid) {
				return Promise.reject(new Error(errorMsgInvalidRange));
			}
		}
		return Promise.resolve();
	},
});

const ignoredKeys = ["categoryImages", "productImages"];

export const trimStrings = (obj) => {
	if (Array.isArray(obj)) {
		return obj.map((item) => (typeof item === "string" ? item.trim() : item));
	}

	if (obj !== null && typeof obj === "object") {
		return Object.fromEntries(
			Object.entries(obj).map(([key, value]) => [
				key,
				ignoredKeys.includes(key) || typeof value !== "string"
					? value
					: value.trim(),
			]),
		);
	}

	return typeof obj === "string" ? obj.trim() : obj;
};

export const ACTION_KEY = {
	EDIT_TEXT_NOTE_MEMBER: "edit_text_note_member",
	DELETE_TEXT_NOTE_MEMBER: "delete_text_note_member",
	EDIT_MEMBER: "edit_member",
	DELETE_MEMBER: "delete_member",
	DOWNLOAD_CSV_MEMBER: "download_csv_member",

	CREATE_AUDIENCE: "create_audience",
	DELETE_AUDIENCE: "delete_audience",

	EDIT_PAGE_SETTING: "edit_page_setting",

	CREATE_COUPON: "create_coupon",
	EDIT_COUPON: "edit_coupon",
	DELETE_COUPON: "delete_coupon",

	CREATE_LOTTO: "create_lotto",
	EDIT_LOTTO: "edit_lotto",
	DELETE_LOTTO: "delete_lotto",

	CREATE_VISIT: "create_VISIT",
	EDIT_VISIT: "edit_VISIT",
	DELETE_VISIT: "delete_VISIT",

	CREATE_EVENT: "create_event",
	EDIT_EVENT: "edit_event",
	DELETE_EVENT: "delete_event",
	SETTING_DISPLAY_EVENT_IN_MEMBERSHIP: "setting_display_event_in_membership",

	DOWNLOAD_CSV_SCHEDULE: "download_csv_schedule",

	CREATE_SURVEY: "create_survey",
	EDIT_SURVEY: "edit_survey",
	DELETE_SURVEY: "delete_survey",
	DOWNLOAD_CSV_SURVEY: "download_csv_survey",

	CREATE_STAMP: "create_stamp",
	RESET_STAMP: "reset_stamp",
	DOWNLOAD_CSV_STAMP: "download_csv_stamp",
};

export const BLOCKED_PERMISSIONS = {
	[ROLES.MASTER]: [],
	[ROLES.MANAGER]: Object.values(ACTION_KEY),
};

export const ALLOW_EXTENSION_IMAGE_FILE = [
	".jpg",
	".jpeg",
	".png",
	"..JPG",
	".JPEG",
	".PNG",
];

export const VALID_IMAGE_MIME_TYPE = ["image/jpeg", "image/png"];

export const isValidImageFile = (file) => {
	if (!file) return false;

	const fileName = file.name || "";
	const extension = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
	const isExtensionValid = ALLOW_EXTENSION_IMAGE_FILE.includes(extension);
	const isMimeTypeValid = VALID_IMAGE_MIME_TYPE.includes(file.type);

	return isExtensionValid && isMimeTypeValid;
};

const isSizeValid = (size, maxSize = 100 * 1024) => {
	return size <= maxSize;
};

const validateImageDimensions = (
	file,
	requiredWidth = 500,
	requiredHeight = 500,
) => {
	return new Promise((resolve) => {
		const img = new Image();

		img.onload = () => {
			const isValid =
				img.width === requiredWidth && img.height === requiredHeight;
			URL.revokeObjectURL(img.src);
			resolve(isValid);
		};

		img.onerror = () => {
			URL.revokeObjectURL(img.src);
			resolve(false);
		};

		const url = URL.createObjectURL(file);
		img.src = url;
	});
};

export const validateStampImages = async (file) => {
	if (!file) return null;

	try {
		const isValidFileType = isValidImageFile(file);

		if (!isValidFileType) {
			return MESSAGE_VALIDATION.FILE_TYPE_INVALID;
		}

		const isValidDimensions = await validateImageDimensions(file);

		if (!isSizeValid(file.size) || !isValidDimensions) {
			return MESSAGE_VALIDATION.STAMP_IMAGE_SIZE_INVALID;
		}
	} catch (error) {
		return MESSAGE_VALIDATION.STAMP_IMAGE_SIZE_INVALID;
	}
};
